import { Component, Input, OnInit } from '@angular/core';
import { ConfigService } from '@finder/core/config.service';
import { CurrencyPipe } from '@angular/common';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { CONFIG_CONSTANTS } from '@finder/shared/constants/app.constants';
import { SITE_ID } from '@finder/shared/constants/app.constants';

@Component({
    selector: 'finder-aag-starting-from-price',
    templateUrl: './aag-starting-from-price.component.html',
    styleUrls: ['./aag-starting-from-price.component.scss'],
})
export class AagStartingFromPriceComponent implements OnInit {

    @Input() startingPrice: any;
    tooltip: any;

    constructor(
        private localeService: LocaleService,
        private currencyPipe: CurrencyPipe,
        private configService: ConfigService,
    ) {}

    ngOnInit() {
        this.formatPricesAndPriceRanges();
        this.formatStartingPriceDining();
        this.createTooltip();
    }

    // Covers Starting Price for dining entities
    formatStartingPriceDining() {
        if (!this.startingPrice.hasOwnProperty('price')) {
            return;
        }

        // Get prices...
        const prices = this.startingPrice.price.match(/[$]?[0-9]+([.][0-9]+)?/g) || [];

        // ... and format them
        prices.forEach((price: string) => {
            this.startingPrice.price = this.formatPricesForDisplay(price);
        });

        this.startingPrice.priceString = this.startingPrice.label.replace('%PRICE%', this.startingPrice.price);
    }

    // Covers exact price or price ranges for non-dining entities (events and tours, etc.)
    // separated into age bands: adult, junior, child, infant, allAges
    formatPricesAndPriceRanges() {
        if (!this.startingPrice.hasOwnProperty('adult') &&
            !this.startingPrice.hasOwnProperty('allAges') &&
            !this.startingPrice.hasOwnProperty('group') &&
            !this.startingPrice.hasOwnProperty('child')
        ) {
            return;
        }

        // String to display pricing info on the AAG
        this.startingPrice.priceString = '';

        // Get and format adult prices
        if (this.startingPrice.hasOwnProperty('adult')) {
            this.retrieveAndFormatPrices('adult');

            // Create adult label
            this.startingPrice.priceString += this.startingPrice.adult.label.replace('%PRICE%', this.startingPrice.adult.price);
        }

        // Get and format allAges prices
        if (this.startingPrice.hasOwnProperty('allAges')) {
            this.retrieveAndFormatPrices('allAges');

            // No label (aside from price value) is shown for allAges data (to accommodate hourly rates, kayaks/golf cart rentals, etc.)
            this.startingPrice.priceString += this.startingPrice.allAges.label.replace('%PRICE%', this.startingPrice.allAges.price);
        }

        // Get and format group prices
        if (this.startingPrice.hasOwnProperty('group')) {
            this.retrieveAndFormatPrices('group');

            // No label (aside from price value) is shown for group data (to accommodate hourly rates, kayaks/golf cart rentals, etc.)
            this.startingPrice.priceString += this.startingPrice.group.label.replace('%PRICE%', this.startingPrice.group.price);
        }

        // Get and format child prices
        if (this.startingPrice.hasOwnProperty('child')) {
            this.retrieveAndFormatPrices('child');

            // Check whether an adult price is set, in which case we need a separator
            if (this.startingPrice.hasOwnProperty('adult')) {
                this.startingPrice.priceString += ', ';
            }

            // Create child label using price and age data
            const childReplacementValues = {
                // replacement key : replacement value
                '%PRICE%': this.startingPrice.child.price,
                AGE_MIN: this.startingPrice.child.minAge,
                AGE_MAX: this.startingPrice.child.maxAge
            };

            this.startingPrice.priceString += this.startingPrice.child.label.replace(
                /%PRICE%|AGE_MIN|AGE_MAX/gi, (matched: string) =>
                    childReplacementValues[matched]
            );
        }

        // Get and format junior prices
        if (this.startingPrice.hasOwnProperty('junior')) {
            this.retrieveAndFormatPrices('junior');

            const juniorReplacementValues = {
                // replacement key : replacement value
                '%PRICE%': this.startingPrice.junior.price,
                AGE_MAX: this.startingPrice.junior.maxAge
            };

            // Create junior label; assume if junior is present we have adult/child info so we need a separator
            this.startingPrice.priceString += ', ' +
                this.startingPrice.junior.label.replace(
                    /%PRICE%|AGE_MAX/gi, (matched: string) =>
                        juniorReplacementValues[matched]
                );
        }

        // Get and format infant prices
        if (this.startingPrice.hasOwnProperty('infant')) {
            this.retrieveAndFormatPrices('infant');

            const infantReplacementValues = {
                // replacement key : replacement value
                '%PRICE%': this.startingPrice.infant.price,
                AGE_MAX: this.startingPrice.infant.maxAge
            };

            // Create infant label; assume if infant is present we have adult/child info so we need a separator
            this.startingPrice.priceString += ', ' +
                this.startingPrice.infant.label.replace(
                    /%PRICE%|AGE_MAX/gi, (matched: string) =>
                        infantReplacementValues[matched]
                );
        }

        this.startingPrice.priceString += ' ' + this.startingPrice.taxNotIncluded;
    }

    retrieveAndFormatPrices(ageBand: string) {
        const prices = this.startingPrice[ageBand].price.match(/[$]?[0-9]+([.][0-9]+)?/g);

        prices.forEach((price: string) => {
            this.startingPrice[ageBand].price = this.startingPrice[ageBand].price.replace(
                price, this.formatPricesForDisplay(price, this.startingPrice[ageBand].currency)
            );
        });
    }

    formatPricesForDisplay(price: string, currency: string = 'USD') {
        const locale = this.localeService.getLocale();
        const isLACD = this.localeService.isLACDRegion();
        const stringPrice = price.replace('$', '');

        let displayPrice = this.currencyPipe.transform(
            parseFloat(stringPrice), // the numeric price value itself
            currency,               // currency (USD, HKD are what we encounter)
            '$',                    // symbol display type (as a string)
            '1.0-2',                // min 1 whole unit, 0 to 2 fractional unit
            // we only want locale-based formatting for WDW's LACD locales, not HKDL or DLR
            isLACD ? locale : ''
        );

        if (isLACD) {
            // desired sample output: $ 100 USD or $100 USD depending on which LACD locale
            displayPrice = `${displayPrice} ${currency}`;
        }

        // HKDL requires currency to display before amount for all locales, including en
        if (this.configService.getValue(CONFIG_CONSTANTS.siteIdKey) === SITE_ID.hkdl) {
            // desired sample output: HKD$100
            displayPrice = `${currency}${displayPrice}`;
        }

        return displayPrice;
    }

    createTooltip() {
        if (this.startingPrice.tooltip) {
            this.tooltip = {
                id: 'starting-price-tooltip',
                name: 'learn-more',
                size: 's',
                color: '#A1AFC0',
                text: this.startingPrice.tooltip,
                position: 'top-center'
            };
        }
    }
}
